import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, createGridDropDownCommitAfter, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { usePRD3012_R01Store } from "./Store_PRD3012_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultPRD3012_R01InspectDto, PRD3012_R01InspectDto } from "./Dto_PRD3012_R01";
import { Info_PRD3012_R01 } from "./Info_PRD3012_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { getAllWorkPlaces } from "src/apis/workPlaceApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { getAllWarehouses } from "src/apis/warehouseApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";

const COLUMN_STATE_KEY = 'PRD3012_R01-inspect-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: [''],
	insertedDisabledCells: [''],
	commonDisabledCells: ['badCase'],
	
	pointerCells: [''],
	insertedPointerCells: ['badCase', 'badQty'],
	commonPointerCells: [''],

	alignLeftCells: [''],
};

const isInspectRowEditable = (data?: PRD3012_R01InspectDto | null) => data?.inserted === true;

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD3012_R01InspectDto> | null>;
	defaultPRD3012_R01InspectRow: PRD3012_R01InspectDto;
}

export const InspectTable_PRD3012_R01 = (props: Props) => {

	const { gridRef, defaultPRD3012_R01InspectRow } = props;

	const { 
			PRD3012_R01InspectList, setPRD3012_R01InspectList, 
			editPRD3012_R01,
			selectedPRD3012_R01InspectList, setSelectedPRD3012_R01InspectList, removeSelectedPRD3012_R01InspectList, addSelectedPRD3012_R01InspectList, initSelectedPRD3012_R01InspectList,
			deletedPRD3012_R01InspectList, setDeletedPRD3012_R01InspectList, addDeletedPRD3012_R01InspectList, removeDeletedPRD3012_R01InspectList,
			insertPRD3012_R01InspectList, updatePRD3012_R01InspectList, removePRD3012_R01InspectList,
			inspectColumnState, setInspectColumnState,
			originalPRD3012_R01InspectList, setOriginalPRD3012_R01InspectList,
		} = usePRD3012_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { faultyValueList } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PRD3012_R01InspectList,
		defaultGridData: defaultPRD3012_R01InspectRow,
		selectedRow: selectedPRD3012_R01InspectList,
		originalGridData: originalPRD3012_R01InspectList,
		updateRow: (index: number, data: any) => updatePRD3012_R01InspectList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		badCaseNm: '불량사유',
		badQty: '불량수량',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<PRD3012_R01InspectDto>({
			idKey: 'valueIndex',
			selectedList: selectedPRD3012_R01InspectList,
			deletedList: deletedPRD3012_R01InspectList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});

		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedPRD3012_R01InspectList, deletedPRD3012_R01InspectList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	const commonInputColumns = ['badCase', 'badQty'];
	const defaultEditableColumns = ['badCase', 'badQty'];

	const handleGridDropDownCommitAfter = useMemo(() =>
		createGridDropDownCommitAfter<PRD3012_R01InspectDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<PRD3012_R01InspectDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
        {
            headerName: '불량사유', colId: 'badCase', field: 'badCase', width: 130,
			...CommonCodeColumn.options(
				faultyValueList,
				(params) => isInspectRowEditable(params.data),
				CommonCodeColumn.change(updatePRD3012_R01InspectList, {
					canChange: ({ data }) => isInspectRowEditable(data),
				}),
				handleGridDropDownCommitAfter,
			),
		},
        {
            headerName: '불량수량', colId: 'badQty', field: 'badQty', width: 130,
			editable: (params) => isInspectRowEditable(params.data),
			pasteable: true,
		},
		// ...IndexParameterCell(),
	], [deletedPRD3012_R01InspectList, PRD3012_R01InspectList, faultyValueList, updatePRD3012_R01InspectList, handleGridDropDownCommitAfter]);

	const columnDefs: ColDef<PRD3012_R01InspectDto>[] = useMemo(() => {
		if (inspectColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<PRD3012_R01InspectDto>({
			baseColumnDefs,
			columnState: inspectColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, inspectColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: PRD3012_R01InspectList,
		originalDataList: originalPRD3012_R01InspectList,
		updateRow: updatePRD3012_R01InspectList,
		customBeforeUpdate: (updatedData, field) => {
			if (!updatedData.inserted) {
				const original = originalPRD3012_R01InspectList.find(
					item => item.valueIndex === updatedData.valueIndex,
				);
				if (original) {
					return {
						...updatedData,
						[field]: original[field as keyof PRD3012_R01InspectDto],
					};
				}
			}
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: PRD3012_R01InspectList,
		idKey: 'valueIndex',
		selectedList: selectedPRD3012_R01InspectList,
		deletedList: deletedPRD3012_R01InspectList,
		addSelected: addSelectedPRD3012_R01InspectList,
		removeSelected: removeSelectedPRD3012_R01InspectList,
		clickable, setClickable
	});

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: PRD3012_R01InspectList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<PRD3012_R01InspectDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return isInspectRowEditable(data) && commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(PRD3012_R01InspectList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<PRD3012_R01InspectDto>[] = useMemo(() => [
	], [editPRD3012_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(PRD3012_R01InspectList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<PRD3012_R01InspectDto>) => {
		postSortHandler(params, PRD3012_R01InspectList, originalPRD3012_R01InspectList, setPRD3012_R01InspectList);
	}, [PRD3012_R01InspectList, originalPRD3012_R01InspectList, ]);

	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = PRD3012_R01InspectList
			.reduce((sum, item) => sum + (deletedPRD3012_R01InspectList.includes(item.itemCd) ? 0 : Number(item.badQty) || 0), 0);

		return [{
			badCaseNm: PRD3012_R01InspectList.length.toLocaleString() + "건",
			badQty: totalQty,
		}];	  
	}, [PRD3012_R01InspectList, deletedPRD3012_R01InspectList,]);

	return (
		<>
			{/* selectedDetailPRD3012_R01 : {JSON.stringify(selectedPRD3012_R01InspectList)}<br /> */}
			
			<GridContextWrapper<PRD3012_R01InspectDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setPRD3012_R01InspectList}
				initSelected={initSelectedPRD3012_R01InspectList}
				defaultRowData={defaultPRD3012_R01InspectRow}
				baseColumnDefs={columnDefs}	
				setColumnState={setInspectColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<PRD3012_R01InspectDto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={gridDefaultColDef}

					rowClassRules={createRowClassRules<PRD3012_R01InspectDto>({
						idKey: 'valueIndex',
						selectedList: selectedPRD3012_R01InspectList,
						deletedList: deletedPRD3012_R01InspectList
					})}

					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setInspectColumnState}		//컬럼 상태 저장
				/>
			</GridContextWrapper>
		</>
	)
}